import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CaseIdSchema, HighlightSchema, type Event, type Highlight } from '@casetrace/shared';
import { deleteDoc, doc, setDoc } from 'firebase/firestore';
import { useAuth } from '../context/auth-context';
import { db } from '../lib/firebase';
import { highlightsQueryKey } from './use-case-highlights';

type ToggleContext = { previous: Map<string, Highlight> | undefined };

/**
 * Tilstanden brukeren faktisk klikket på. Den må sendes inn eksplisitt:
 * onMutate kjører før mutationFn, så cachen er allerede endret optimistisk
 * og kan ikke brukes til å utlede om vi skal opprette eller slette.
 */
export type ToggleHighlightInput = {
  readonly event: Event;
  readonly isHighlighted: boolean;
};

export type ToggleHighlightHandler = (event: Event, isHighlighted: boolean) => void;

function buildHighlightPayload(event: Event, caseId: string, uid: string): Highlight {
  return HighlightSchema.parse({
    eventId: event.id,
    caseId: CaseIdSchema.parse(caseId),
    timestamp: event.timestamp,
    title: event.title,
    type: event.type,
    createdBy: uid,
    createdAt: new Date().toISOString(),
  });
}

export function useToggleHighlight(caseId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const queryKey = highlightsQueryKey(caseId);

  return useMutation({
    mutationFn: async ({ event, isHighlighted }: ToggleHighlightInput) => {
      const parsedCaseId = CaseIdSchema.parse(caseId);
      const ref = doc(db, 'cases', parsedCaseId, 'highlights', event.id);
      if (isHighlighted) {
        await deleteDoc(ref);
        return;
      }
      const uid = user?.uid;
      if (!uid) throw new Error('Du må være innlogget for å markere relevante hendelser.');
      await setDoc(ref, buildHighlightPayload(event, caseId, uid));
    },
    onMutate: async ({ event, isHighlighted }: ToggleHighlightInput): Promise<ToggleContext> => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Map<string, Highlight>>(queryKey);
      const next = new Map(previous ?? []);
      const uid = user?.uid;

      if (isHighlighted) {
        next.delete(event.id);
      } else if (uid) {
        next.set(event.id, buildHighlightPayload(event, caseId, uid));
      }

      queryClient.setQueryData(queryKey, next);
      return { previous };
    },
    onError: (_error, _event, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
  });
}
